import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';

import { TokenSearchResponse, TokensByNameOrSymbolQuery } from '../utils/0x/token-registry.types';
import { fetcher } from '../utils/fetcher';
import { routes } from '../utils/routes';
import { objectToQueryString } from '../utils/string';

const DEFAULT_RESULTS_LIMIT = 10;

interface UseSearchTokensParams {
  query?: string;
  chainIds?: number[];
  limit?: number;
}

interface UseSearchTokensArgs extends UseSearchTokensParams {
  /** Effective per-page limit (defaults to DEFAULT_RESULTS_LIMIT). */
  effectiveLimit: number;
}

const buildUrl = ({ query, chainIds, effectiveLimit }: UseSearchTokensArgs, page: number) => {
  const parameters: TokensByNameOrSymbolQuery = {
    chainId: chainIds,
    limit: effectiveLimit,
    page, // 0x API uses 1-based pagination
    query: query ?? '',
  };

  return `${routes.api.SEARCH_TOKENS}${objectToQueryString(parameters)}`;
};

export const searchTokensQueryKey = ({ query, chainIds, limit }: UseSearchTokensParams) =>
  ['search-tokens', { query, chainIds, limit }] as const;

export const useSearchTokens = ({
  query,
  chainIds,
  limit = DEFAULT_RESULTS_LIMIT,
}: UseSearchTokensParams): UseInfiniteQueryResult<
  { pages: TokenSearchResponse[]; pageParams: number[] },
  Error
> & { fetchNextPage: () => Promise<unknown> } => {
  const canSearch = !!query;

  return useInfiniteQuery<
    TokenSearchResponse,
    Error,
    { pages: TokenSearchResponse[]; pageParams: number[] },
    ReturnType<typeof searchTokensQueryKey>,
    number
  >({
    queryKey: searchTokensQueryKey({ query, chainIds, limit }),
    queryFn: ({ pageParam }) =>
      fetcher(buildUrl({ query, chainIds, effectiveLimit: limit }, pageParam)),
    initialPageParam: 1,
    // Bump the page number until the API returns fewer than `limit` items.
    getNextPageParam: (lastPage, allPages) => {
      if ('data' in lastPage && lastPage.data && lastPage.data.length >= limit) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: canSearch,
    staleTime: Infinity,
    refetchOnReconnect: false,
  });
};
